import {memo, useContext} from "react";
import {
	Pagination,
	Table
} from "antd";
import {
	Image,
	StatusLabel
} from "~/components";
import {
	arrayToMap,
	numberFormat
} from "~/utils";
import {AppContext} from "~/context/AppProvider";

const MKT_SERVICE_LABELS = {
	'ads-google': 'QC Google',
	'ads-facebook': 'QC Facebook',
	'ads-tiktok': 'QC TikTok',
	'admin-facebook': 'QT Facebook',
	'admin-website': 'QT Website',
	'seo': 'SEO',
};

const money = (v) => numberFormat(v || 0);

//Chi tiết lương marketing (hàng mở rộng)
function renderMarketing(item) {
	const m = item.marketing;
	if (!m) return null;
	const services = Object.keys(MKT_SERVICE_LABELS);
	return (
		<div style={{padding: '8px 12px'}}>
			<div style={{display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 10}}>
				<div><b>Doanh số cá nhân:</b> {money(m.personalRevenue)}đ <span style={{color: '#888'}}>(phí DV {money(m.serviceFeeTotal)} + 20% Google {money(m.googleAdd)})</span></div>
				<div><b>Lương cơ bản:</b> {money(m.baseSalary)}đ</div>
				<div><b>Ngân sách chạy:</b> Google {money(m.googleBudget)} / FB {money(m.facebookBudget)}</div>
				<div><b>Thưởng FB:</b> {money(m.facebookBonus)}đ</div>
			</div>
			<table style={{borderCollapse: 'collapse', width: 'auto'}}>
				<thead>
					<tr style={{background: '#f1f5f9'}}>
						<th style={{textAlign: 'left', padding: '4px 12px'}}>Dịch vụ</th>
						{services.map((s) => <th key={s} style={{textAlign: 'right', padding: '4px 12px'}}>{MKT_SERVICE_LABELS[s]}</th>)}
					</tr>
				</thead>
				<tbody>
					<tr>
						<td style={{padding: '4px 12px'}}>Phí dịch vụ</td>
						{services.map((s) => <td key={s} style={{textAlign: 'right', padding: '4px 12px'}}>{money(m.serviceFees?.[s])}</td>)}
					</tr>
					<tr>
						<td style={{padding: '4px 12px'}}>Hoa hồng</td>
						{services.map((s) => <td key={s} style={{textAlign: 'right', padding: '4px 12px', color: 'var(--green)'}}>{money(m.commission?.[s])}</td>)}
					</tr>
				</tbody>
			</table>
			{item.managers && typeof item.managers.groupRevenue !== 'undefined' && (
				<div style={{marginTop: 10}}>
					<b>Quản lý:</b> Doanh số phòng {money(item.managers.groupRevenue)}đ — Lương QL {money(item.managers.managerBase)}đ + Hoa hồng QL {money(item.managers.managerCommission)}đ = <b>{money(item.managers.total)}đ</b>
				</div>
			)}
		</div>
	);
}

function PayTable({items = [], listGroup, department, onDetail, onManager, onExpense, pagination, onPaginationChange, openModal, onWorkday}) {

	const {tableHeight} = useContext(AppContext);

	const groupMap = arrayToMap(listGroup);

	const isMarketing = department === 'marketing';

	const handleDetailClick = (item, service) => {
		onDetail(item, service);
		openModal('detail');
	}

	const handleManagerClick = (item) => {
		onManager(item);
		openModal('manager');
	}

	const handleExpenseClick = (item, type) => {
		onExpense(item, type);
		openModal('expense');
	}

	const handleWorkDayClick = (item, type) => {
		onWorkday(item, type);
		openModal('workday');
	}

	//Cột hoa hồng dịch vụ website (mặc định)
	const websiteCommissionColumns = [
		{ title: 'Website', key: "website", width: '100px', render: (_, item) => (
			<div className="pointer" onClick={() => handleDetailClick(item, 'website')}>{numberFormat(item?.commission?.website)}</div>
		)},
		{ title: 'Host', key: "host", width: '100px', render: (_, item) => (
			<div className="pointer" onClick={() => handleDetailClick(item, 'host')}>{numberFormat(item?.commission?.host)}</div>
		)},
		{ title: 'SSL', key: "ssl", width: '100px', render: (_, item) => (
			<div className="pointer" onClick={() => handleDetailClick(item, 'ssl')}>{numberFormat(item?.commission?.ssl)}</div>
		)},
		{ title: 'Email', key: "Email", width: '100px', render: (_, item) => (
			<div className="pointer" onClick={() => handleDetailClick(item, 'email')}>{numberFormat(item?.commission?.email)}</div>
		)},
		{ title: 'BCT', key: "moit", width: '100px', render: (_, item) => (
			<div className="pointer" onClick={() => handleDetailClick(item, 'moit')}>{numberFormat(item?.commission?.moit)}</div>
		)},
	];

	//Cột hoa hồng dịch vụ marketing (khi lọc phòng Marketing)
	const marketingCommissionColumns = [
		...Object.entries(MKT_SERVICE_LABELS).map(([key, title]) => ({
			title, key, width: '105px', render: (_, item) => (
				<div className="pointer" onClick={() => handleDetailClick(item, key)}>{money(item?.marketing?.commission?.[key])}</div>
			),
		})),
		{ title: 'Thưởng FB', key: 'fbBonus', width: '110px', render: (_, item) => (
			<span style={{color: 'var(--green)'}}>{money(item?.marketing?.facebookBonus)}</span>
		)},
	];

	const commissionColumns = isMarketing ? marketingCommissionColumns : websiteCommissionColumns;

	const columns = [
		{ title: 'Nhân viên', dataIndex: 'user', key: "user", width: '300px', render: (_, item) => (
			<div className="d-flex gap align-items">
				<div className="avatar"><Image src={item.user?.avatar} /></div>
				<div>
					<b className={(item.role?.key === 'salesManager') ? 'color-theme' : undefined }>{item.user?.firstname + ' ' + item.user?.lastname}</b>
					<p>{item.user?.username}</p>
					<p>{(item.groupId !== 0) && groupMap[item.groupId]?.name } {<StatusLabel small type={item.role?.badge}>{item.role?.label}</StatusLabel>}</p>
				</div>
			</div>
		)},
		{ title: 'Lương cơ bản', dataIndex: 'salary', key: "salary", width: '140px', render: (_, item) => (
			//Backend đã đặt salary = mức lương cơ bản đầy đủ của bên thắng (lương cứng hoặc mốc)
			<div>
				<div>{numberFormat(item?.salary)}</div>
				{item?.hardSalaryPay > 0 && (
					<p style={{fontSize: 11, color: '#64748b', margin: 0}}>{item?.salaryByHard ? 'theo lương cứng' : 'theo mốc'}</p>
				)}
			</div>
		)},
		{ title: 'Ngày công', dataIndex: 'dayWorking', key: "dayWorking", width: '110px', render: (_, item) => (
			<div className="pointer" onClick={() => handleWorkDayClick(item)}>{item?.dayWorking} / {item?.day}</div>
		)},
		{ title: 'Lương thực tế', dataIndex: 'pay', key: "pay", width: '130px', render: (_, item) => (numberFormat(item?.pay))},
		...commissionColumns,
		{ title: 'Lương QL', dataIndex: 'manager', key: "manager", width: '130px', render: (_, item) => (
			<div className="pointer" onClick={() => handleManagerClick(item)}>{numberFormat(item?.manager)}</div>
		)},
		{ title: 'Trừ', dataIndex: 'minus', key: "minus", width: '100px', render: (_, item) => (
			<div className="pointer" onClick={() => handleExpenseClick(item, 'minus-wage')}>{numberFormat(item?.minusPay)}</div>
		)},
		{ title: 'Cộng', dataIndex: 'plus', key: "plus", width: '100px', render: (_, item) => (
			<div className="pointer" onClick={() => handleExpenseClick(item, 'plus-wage')}>{numberFormat(item?.plusPay)}</div>
		)},
		{ title: 'Thực lảnh', dataIndex: 'received', key: "received", width: '150px', render: (_, item) => (
			<>{numberFormat(item?.received)}</>
		)},
		{ title: 'Điểm', dataIndex: 'coin', key: "coin", width: '150px', render: (_, item) => (
			<>{numberFormat(item?.coin)}</>
		)},
	];

	return (
		<div className="table-wrapper">
			<div className="table-box">
				<Table columns={columns} rowKey="id" dataSource={items} scroll={{ y:tableHeight, x: 'max-content' }} pagination={false}
					expandable={{
						expandedRowRender: renderMarketing,
						rowExpandable: (item) => !!item.marketing,
					}}
				/>
			</div>
			<Pagination current={pagination.page} defaultCurrent={1} defaultPageSize={20} pageSize={pagination.limit} total={pagination.total} onChange={onPaginationChange}  showSizeChanger={false} />
		</div>
	)
}

export default memo(PayTable);